const scannedStatuses = new Set([
  'entregado',
  'entregada',
  'completado',
  'completada',
  'finalizado',
  'finalizada'
]);
const incidentStatuses = new Set(['incidencia', 'error', 'fallido', 'fallida']);

export const packageScanStates = {
  scanned: 'scanned',
  pending: 'pending',
  incident: 'incident'
};

export const taskFinalizeStatuses = {
  finalized: 'FINALIZADO',
  incident: 'INCIDENCIA',
  pending: 'PENDIENTE',
  cancelled: 'ANULADO'
};

export function createPackageScanItem(source) {
  const task = mapRouteTaskResponse(source);
  const state = mapTaskState(task.status);

  return {
    key: task.id || task.barcode,
    task,
    state,
    incidentDescription: '',
    incidentCode: '',
    isActive: false,
    isIncidentEditorOpen: false
  };
}

export function calculatePackageCounters(packages) {
  const deliveredCount = packages.filter((item) => item.state === packageScanStates.scanned).length;
  const incidentCount = packages.filter((item) => item.state === packageScanStates.incident).length;
  const missingCount = packages.length - deliveredCount - incidentCount;

  return {
    deliveredCount,
    incidentCount,
    missingCount,
    scannedPackages: deliveredCount + incidentCount,
    totalPackages: packages.length
  };
}

export function buildPackageScanChips({ routeStop, packages, date = new Date() }) {
  return [
    formatDateChip(date),
    formatPackageCount(routeStop?.stop.totalTasks ?? packages.length),
    formatWeight(packages.reduce((sum, item) => sum + item.task.weight, 0))
  ].filter(Boolean);
}

export function formatPackageCount(packageCount) {
  const normalizedCount = Math.max(0, packageCount);
  const noun = normalizedCount === 1 ? 'Paquete' : 'Paquetes';
  return `${normalizedCount} ${noun}`;
}

export function formatWeight(weight) {
  if (weight <= 0) {
    return '';
  }

  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0
  }).format(weight)} Kg`;
}

export function packageStatusText(state) {
  if (state === packageScanStates.scanned) {
    return 'Finalizado';
  }

  if (state === packageScanStates.incident) {
    return 'Incidencia';
  }

  return 'Pendiente';
}

export function packageStatusClass(state) {
  return {
    'scan-package-card__status--success': state === packageScanStates.scanned,
    'scan-package-card__status--warning': state === packageScanStates.incident
  };
}

export function findPackageByBarcode(packages, barcode) {
  const normalizedBarcode = normalizeBarcode(barcode);

  if (!normalizedBarcode) {
    return null;
  }

  return packages.find((item) =>
    normalizeBarcode(item.task.barcode).toLowerCase() === normalizedBarcode.toLowerCase()
  ) ?? null;
}

export function markPackageFinalized(item) {
  return {
    ...item,
    state: packageScanStates.scanned,
    task: {
      ...item.task,
      status: taskFinalizeStatuses.finalized
    },
    incidentDescription: '',
    incidentCode: '',
    isIncidentEditorOpen: false
  };
}

export function markPackageIncident(item, incident) {
  return {
    ...item,
    state: packageScanStates.incident,
    task: {
      ...item.task,
      status: taskFinalizeStatuses.incident
    },
    incidentDescription: incident.description,
    incidentCode: incident.code,
    isIncidentEditorOpen: false
  };
}

export function buildTaskScanDraft({ planId, routeStop, routeStopId, packages }) {
  const routeStopEntity = routeStop?.stop;

  return {
    planId: routeStopEntity?.planId || planId,
    routeStopId: routeStopEntity?.id || routeStopId,
    routeStopKey: routeStopEntity?.stopId || routeStopId,
    mode: 'TaskScan',
    status: 'FINALIZADO',
    incidentDescription: '',
    incidentCode: '',
    observations: '',
    tasks: packages.map(mapTaskDraft),
    images: []
  };
}

export function buildIncidentOptions(incidentEvents, routeStop) {
  const scope = resolveRouteStopScope(routeStop);
  const validEvents = incidentEvents
    .filter((event) =>
      equalsText(event.type, 'INCIDENCIA') &&
      equalsText(event.scope, scope) &&
      equalsText(event.entity, 'PAQUETE') &&
      event.code?.trim() &&
      event.description?.trim()
    );

  const duplicateDescriptions = validEvents
    .reduce((lookup, event) => {
      const key = event.description.trim().toLowerCase();
      lookup.set(key, (lookup.get(key) ?? 0) + 1);
      return lookup;
    }, new Map());

  return validEvents.map((event) => {
    const description = event.description.trim();
    const code = event.code.trim();
    const hasDuplicateDescription = (duplicateDescriptions.get(description.toLowerCase()) ?? 0) > 1;

    return {
      code,
      description,
      displayText: hasDuplicateDescription ? `${description} (${code})` : description
    };
  });
}

export function normalizeStoredIncidentEvents(rawValue) {
  return Array.isArray(rawValue) ? rawValue : [];
}

function mapRouteTaskResponse(source) {
  return {
    id: readString(source, '_id', 'id'),
    barcode: readString(source, 'barcode'),
    shipmentExternalId: readString(source, 'shipment_external_id', 'shipmentExternalId'),
    stopId: readString(source, 'stop_id', 'stopId'),
    createdAt: readString(source, 'created_at', 'createdAt'),
    shipmentReference: readString(source, 'shipment_reference', 'shipmentReference'),
    weight: readNumber(source, 'weight'),
    volumetricWeight: readNumber(source, 'volumetric_weight', 'volumetricWeight'),
    measurements: readString(source, 'measurements'),
    note1: readString(source, 'note_1', 'note1'),
    note2: readString(source, 'note_2', 'note2'),
    status: readString(source, 'status'),
    updatedAt: readString(source, 'updated_at', 'updatedAt')
  };
}

function mapTaskState(status) {
  const normalizedStatus = normalize(status);

  if (scannedStatuses.has(normalizedStatus)) {
    return packageScanStates.scanned;
  }

  if (incidentStatuses.has(normalizedStatus)) {
    return packageScanStates.incident;
  }

  return packageScanStates.pending;
}

function mapTaskDraft(item) {
  return {
    id: item.task.id,
    stopId: item.task.stopId,
    shipmentExternalId: item.task.shipmentExternalId,
    shipmentReference: item.task.shipmentReference,
    barcode: item.task.barcode,
    weight: item.task.weight,
    status: mapDraftTaskStatus(item),
    incidentDescription: item.incidentDescription || '',
    incidentCode: item.incidentCode || ''
  };
}

function mapDraftTaskStatus(item) {
  if (item.state === packageScanStates.scanned) {
    return taskFinalizeStatuses.finalized;
  }

  if (item.state === packageScanStates.incident) {
    return taskFinalizeStatuses.incident;
  }

  return taskFinalizeStatuses.pending;
}

function resolveRouteStopScope(routeStop) {
  const routeStopType = routeStop?.stop.type ?? '';
  const normalizedType = normalize(routeStopType);
  return normalizedType === 'recogida' || normalizedType === 'recoleccion'
    ? 'RECOGIDA'
    : 'ENTREGA';
}

function formatDateChip(date) {
  const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${monthNames[date.getMonth()]}`;
}

function normalizeBarcode(barcode) {
  return barcode?.trim?.() ?? '';
}

function equalsText(left, right) {
  return normalize(left) === normalize(right);
}

function readString(source, ...keys) {
  const value = readValue(source, keys);
  return typeof value === 'string' ? value.trim() : '';
}

function readNumber(source, ...keys) {
  const value = Number(readValue(source, keys));
  return Number.isFinite(value) ? value : 0;
}

function readValue(source, keys) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }

  return undefined;
}

function normalize(value) {
  return value?.trim?.().toLowerCase() ?? '';
}
